import { useTranslation } from "react-i18next"

import type {
  SharePointConnectionVerify,
  VerifyStepStatus,
} from "../hooks/use-sharepoint-connection-verify"

import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  FileText,
  FolderOpen,
  Loader2,
  XCircle,
} from "@/components/icons"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Spinner } from "@/components/ui/spinner"
import { formatBytes } from "@/features/chat/components/file-picker-utils"

function StepIcon({ status }: { status: VerifyStepStatus }) {
  if (status === "running") return <Loader2 className="text-primary size-3.5 animate-spin" />
  if (status === "ok") return <CheckCircle2 className="text-success size-3.5" />
  if (status === "error") return <XCircle className="text-destructive size-3.5" />
  return <div className="border-border size-3.5 rounded-full border-2" />
}

function VerifyStep({
  status,
  label,
  error,
}: {
  status: VerifyStepStatus
  label: string
  error?: string
}) {
  return (
    <div className="flex items-start gap-2">
      <StepIcon status={status} />
      <div className="min-w-0 flex-1">
        <span className={status === "error" ? "text-destructive" : undefined}>{label}</span>
        {error && <p className="text-destructive">{error}</p>}
      </div>
    </div>
  )
}

type SharePointVerifyConnectionProps = {
  verify: SharePointConnectionVerify
  /** Whether the current form holds enough auth/target data to run the check. */
  canVerify: boolean
  onVerify: () => void
}

/** "Verify Connection" box: run button plus the 3-step progress list. */
export function SharePointVerifyConnection({
  verify,
  canVerify,
  onVerify,
}: SharePointVerifyConnectionProps) {
  const { t } = useTranslation()
  const { verifying, verifyState, verifyDone, allStepsOk, browseResult, itemKind } = verify

  if (!canVerify) return null

  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">
          {t("adminDashboard.libraries.connectionVerify.title", "Verify Connection")}
        </p>
        <Button
          disabled={verifying}
          size="sm"
          type="button"
          variant={allStepsOk ? "default" : "outline"}
          onClick={onVerify}
        >
          {verifying && (
            <>
              <Spinner className="mr-1 size-3" />
              {t("adminDashboard.libraries.connectionVerify.verifying", "Verifying...")}
            </>
          )}
          {!verifying &&
            verifyDone &&
            t("adminDashboard.libraries.connectionVerify.reVerify", "Re-verify")}
          {!verifying &&
            !verifyDone &&
            t("adminDashboard.libraries.connectionVerify.verify", "Verify")}
        </Button>
      </div>

      {verifyDone && (
        <div className="space-y-1.5 text-xs">
          <VerifyStep
            error={verifyState.step1Error}
            status={verifyState.step1}
            label={t(
              "adminDashboard.libraries.connectionVerify.step1Label",
              "Secret exists in Key Vault"
            )}
          />
          <VerifyStep
            error={verifyState.step2Error}
            status={verifyState.step2}
            label={t(
              "adminDashboard.libraries.connectionVerify.step2Label",
              "Credentials can obtain Azure AD token"
            )}
          />
          <VerifyStep
            error={verifyState.step3Error}
            status={verifyState.step3}
            label={
              itemKind === "list_items"
                ? t(
                    "adminDashboard.libraries.connectionVerify.step3LabelItems",
                    "Can list items in SharePoint list"
                  )
                : t(
                    "adminDashboard.libraries.connectionVerify.step3Label",
                    "Can list files in SharePoint library"
                  )
            }
          />
          {/* Permission-read scope, only checked when replicate_permissions is on */}
          {browseResult?.permissions_scope_ok === false && (
            <VerifyStep
              error={browseResult.permissions_scope_error ?? undefined}
              status="error"
              label={t(
                "adminDashboard.libraries.connectionVerify.permissionsScopeLabel",
                "Can read document permissions"
              )}
            />
          )}
        </div>
      )}
    </div>
  )
}

type SharePointFileBrowserProps = {
  verify: SharePointConnectionVerify
  onLoadMore: () => void
}

/** Collapsible file list populated by step 3 of the verify run. Renders nothing before that. */
export function SharePointFileBrowser({ verify, onLoadMore }: SharePointFileBrowserProps) {
  const { t } = useTranslation()
  const {
    browseOpen,
    setBrowseOpen,
    browseResult,
    browseError,
    browseNextPageToken,
    isLoadingMore,
  } = verify

  if (!browseResult) return null
  const listsItems = (browseResult.item_kind ?? verify.itemKind) === "list_items"

  return (
    <Collapsible open={browseOpen} onOpenChange={setBrowseOpen}>
      <CollapsibleTrigger asChild>
        <Button className="w-full justify-between" size="sm" type="button" variant="outline">
          <span className="flex items-center gap-2">
            <FolderOpen className="size-4" />
            {listsItems
              ? t(
                  "adminDashboard.libraries.connectionVerify.browseItems",
                  "Browse SharePoint items"
                )
              : t(
                  "adminDashboard.libraries.connectionVerify.browseFiles",
                  "Browse SharePoint Files"
                )}
            <Badge className="text-xs" variant="secondary">
              {browseResult.files.length}
              {browseNextPageToken ? "+" : ""}{" "}
              {listsItems
                ? t("adminDashboard.libraries.connectionVerify.items", "items")
                : t("adminDashboard.libraries.connectionVerify.files", "files")}
            </Badge>
          </span>
          {browseOpen ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-2">
        {browseError && (
          <Alert className="mb-2" variant="destructive">
            <XCircle className="size-4" />
            <AlertDescription>{browseError}</AlertDescription>
          </Alert>
        )}
        <ScrollArea className="h-[180px] rounded-md border">
          <ul className="divide-y text-xs">
            {browseResult.files.map((file, idx) => (
              <li key={idx} className="flex items-center gap-2 px-3 py-2">
                <FileText className="text-muted-foreground size-3 shrink-0" />
                <span className="min-w-0 flex-1 truncate font-medium">{file.name}</span>
                <span className="text-muted-foreground shrink-0">{formatBytes(file.size)}</span>
              </li>
            ))}
          </ul>
        </ScrollArea>
        {browseNextPageToken && (
          <Button
            className="mt-2 w-full"
            disabled={isLoadingMore}
            size="sm"
            type="button"
            variant="ghost"
            onClick={onLoadMore}
          >
            {isLoadingMore ? (
              <>
                <Spinner className="mr-1 size-3" />
                {t("adminDashboard.libraries.connectionVerify.loadingMore", "Loading more...")}
              </>
            ) : (
              t("adminDashboard.libraries.connectionVerify.loadMore", "Load more")
            )}
          </Button>
        )}
      </CollapsibleContent>
    </Collapsible>
  )
}
